import React, { useEffect } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import useSession, { useCustomer } from 'hooks/useSession'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import { useCountries } from 'hooks/options/useCountries'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { trpc } from 'api'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { AddressVatSchema } from 'utils/vatSchema'
import { markDetailSectionSaved } from '@user/customer/confirmation/markDetailSectionSaved'

interface Props {
  open: boolean
  close: () => void
}

export default function AddressModal({ open, close }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'user'])
  const config = useRemoteConfig()
  const customer = useCustomer()
  const countries = useCountries()
  const address = customer?.address || undefined

  const defaultValues = {
    street: address?.street || '',
    street2: address?.street2 || '',
    city: address?.city || '',
    zip: address?.zip || '',
    country_code: address?.country_code || config?.region.countryCode || '',
    vat: address?.vat || '',
  }

  const form = useForm({
    defaultValues,
    resolver: zodResolver(AddressVatSchema),
  })

  const isChanged = form.formState.isDirty
  const styleChanged = useAnimatedOpacity(isChanged)

  const setCustomerData = useSession((s) => s.setCustomerData)
  const patch = trpc.customer.patch.useMutation({
    onSuccess: (res) => {
      setCustomerData(res)
      markDetailSectionSaved('address')
      onClose()
    },
  })

  const onSubmit = form.handleSubmit((values) => {
    if (!customer) return
    patch.mutate({ id: customer.id, address: values })
  })

  const onClose = () => {
    form.reset()
    close()
  }

  useEffect(() => {
    if (open) {
      form.reset(defaultValues)
    }
  }, [open])

  const hasVat = config?.region?.vat !== 'hidden'

  return (
    <ModalBottom
      open={open}
      close={close}
      title={t('modals:address.title')}
      desc={t('modals:address.desc')}
      body={
        <FormProvider {...form}>
          <View className="gap-6 px-16">
            {hasVat && (
              <FormInput
                name="vat"
                control={form.control}
                next="street"
                label={t('user:vat')}
              />
            )}
            <FormInput
              control={form.control}
              label={t('user:street')}
              name="street"
            />
            <FormInput
              control={form.control}
              label={t('user:street2')}
              name="street2"
            />
            <FormInput
              control={form.control}
              label={t('user:city')}
              name="city"
            />
            <FormInput
              control={form.control}
              label={t('user:zip')}
              name="zip"
            />
            <FormSelect
              control={form.control}
              label={t('user:country')}
              name="country_code"
              options={countries}
              placeholder={t('user:address.country')}
            />
          </View>
        </FormProvider>
      }
      buttons={
        <>
          <Button variant="outlined" label={t('ui:close')} onPress={onClose} />
          {isChanged && (
            <Button
              variant="filled"
              label={t('ui:save')}
              iconEnd="arrow-narrow-right"
              loading={patch.isPending}
              onPress={onSubmit}
              style={styleChanged}
            />
          )}
        </>
      }
    />
  )
}
